/** @module csi/localeLoader */
import csi from "csi/main";
import LocaleResolver from "csi/config/LocaleResolver";
import LocaleResources from "csi/config/LocaleResources";
import locale from "csi/locale";
import { sessionStore } from "csi/state/rootStore";
import sessionConstants from "csi/sessionConstants";

// Object: mapping of base LocaleResources by locale
const _baseResources = {};

/**
 * Set global state if loaded merged resources
 */
function _onLoadMerged(): void {
	sessionStore.setMergedLocale(true);
}

/**
 * Load development resources
 */
function _loadDev(): Promise<any> {
	return csi.loadScript(csi.urlForModule("nls/en"));
}

/**
 * Load resources for locale
 */
function _loadResources(loc: string, bBase: boolean): Promise<LocaleResources> {
	// if asking for base resources and already allocated, then return immediately
	let resources = bBase && _baseResources[loc];
	if (resources) {
		return Promise.resolve(resources);
	}

	locale.loaderLocaleResources = resources = new LocaleResources(loc);

	if (bBase) {
		_baseResources[loc] = resources;
	}

	// try loading generated file unless set as development environment (setting does not matter when loading base resources);
	// if that doesn't work, then load from English source files
	let mergedUrl = csi.urlForGenerated(loc + (bBase ? "-base" : ""), true);
	let promise;

	if (bBase) {
		promise = csi.loadScript(mergedUrl);
	} else if (sessionConstants.dev) {
		promise = _loadDev();
	} else {
		promise = csi.loadScript(mergedUrl)
			.then(
				_onLoadMerged,
				(err) => {
					// if locale has two parts (A-B) and cannot be loaded, then try stripping off second part and just load first (A)
					const loc2 = loc.replace(/_(.*)$/, "");
					if ((loc2 != loc) && !bBase) {
						locale.loaderLocaleResources = resources = new LocaleResources(loc2);
						mergedUrl = csi.urlForGenerated(loc2, true);
						return csi.loadScript(mergedUrl)
							.then(_onLoadMerged, _loadDev);
					}

					return _loadDev();
				},
			);
	}

	// return resources no matter whether loaded successfully or not
	const onLoadEnd = (): LocaleResources => {
		locale.loaderLocaleResources = null;
		return resources;
	};

	return promise.then(onLoadEnd, onLoadEnd);
}

/**
 * Load resources for locale to be used on page (should only be called once)
 */
export async function initLocale(loc: string): Promise<void> {
	return _loadResources(loc || locale.ENGLISH, false)
		.then((resources) => {
			sessionStore.setLocale(resources.locale);
			locale.setResources(resources);
		});
}

/**
 * Create resolver for given locale using base (out-of-the-box) resources
 */
export function initBaseResolver(loc: string, params: object): Promise<LocaleResolver> {
	return _loadResources(loc, true)
		.then((resources) => new LocaleResolver(resources, params));
}